// External
import { HttpClient } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

// Internal
import { SupportMgmtService } from '../../requests/connect-user-support/connect-user-support.service';
import { ValuesSupportService } from '../../../../pages/support/values/values-support.service';
import { LanguageService } from '../../core/language.service';
import { ValuesService } from '../../../../common/values/values.service';
import { ConfigService } from '../../../../common/config/config.service';
import { AdobeDataLayerService } from '../../core/adobe.datalayer.service';
import { LinksService } from '../../../../common/links/links.service';
import { UsefulService } from '../../global/useful/useful.service';

type SecurityNewsArticle = {
    picture: string,
    title: string,
    description: string,
    url: string,
    text_url: string,
    time_to_read: string,
};

type SecurityNewsResponse = {
    total: number,
    offset: number,
    limit: number,
    data: SecurityNewsArticle[]
};

@Injectable({
    providedIn: 'root'
})

export class SupportService {

    private results = {};
    private articles = {};
    private securityNewsArticles: SecurityNewsArticle[] = [];
    private searchResults = {
        randomId: {
            list: [],
            totalResults: '',
            noPage: 1
        }
    };

    public readonly renderer: Renderer2;

    constructor(
        private readonly http: HttpClient,
        private readonly supportMgmtService: SupportMgmtService,
        private readonly valuesSupportService: ValuesSupportService,
        private readonly languageService: LanguageService,
        private readonly valuesService: ValuesService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly configService: ConfigService,
        public readonly rendererFactory: RendererFactory2,
        private readonly linksService: LinksService,
        private readonly usefulService: UsefulService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /**
     * Function that gets all result based on search made
     * @public
     * @memberof SupportService
     *
     * @param {number} noPage
     * @param {Array} arrayOfWords
     * @param {string} term
     * @param {string} randomId
     * @returns {Observable}
     */
    public listSearchResults(noPage, arrayOfWords, term, randomId): Observable<object> {
        const maxResults = 100;
        const currentLang = this.languageService.getLang();
        const lang = this.valuesSupportService.searchArticlesLanguages[currentLang] ?? this.valuesService.defaultLanguage;

        if (this.results?.[randomId]?.[lang]?.[term]?.[noPage]) {
            this.searchResults[randomId] = this.results[randomId][lang][term][noPage];
            return of(this.searchResults);
        } else {
            this.results[randomId] = this.results[randomId] ?? {};
            this.results[randomId][lang] = this.results[randomId][lang] ?? {};
            this.results[randomId][lang][term] = this.results[randomId][lang][term] ?? {};

            return this.supportMgmtService.getSearchResults(noPage, lang, arrayOfWords)
            .pipe(
                map(resp => {
                        this.results[randomId][lang][term][noPage] = {};
                        this.results[randomId][lang][term][noPage]['list'] = resp.result.items;
                        this.results[randomId][lang][term][noPage]['noPage'] = noPage;
                        this.results[randomId][lang][term][noPage]['totalResults'] = Math.min(parseInt(resp.result.totalResults, 10), maxResults);

                        this.searchResults[randomId] = this.results[randomId][lang][term][noPage];
                        return of(this.searchResults);
                }),
                catchError(err => {
                    throw err;
                })
            );
        }
    }

    /**
     * Function that gets all articles
     * @public
     * @memberof SupportService
     *
     * @param {string} id
     * @returns {Observable}
     */
    public listOfArticles(id): Observable<object|boolean> {
        if (this.getArticles()[id]) {
            return of(true);
        }

        return this.supportMgmtService.getArticle(id)
            .pipe(
                map(
                    resp => {
                        // aici vine un singur articol si punem resp[0]
                        // e atunci cand se alege un articol din lista de articole
                        this.articles[id] = resp[0];
                        return of(this.articles);
                    }
                ),
                catchError(err => {
                    throw err;
                })
            );
    }

    /**
    * This gets articles for security news section in Dashboard page
    * @public
    * @memberof SupportService
    *
    * @param {none}
    * @returns {Observable}
    */
    public listSecurityArticles(): Observable<object> {
        const securityNewsUrl = this.linksService.getSecurityNewsUrl();

        if (!securityNewsUrl) {
            this.securityNewsArticles = [];
        }

        if ((Array.isArray(this.securityNewsArticles) && this.securityNewsArticles.length) || !securityNewsUrl || !this.configService.getDashboardSecurityNews()) {
            return of(this.securityNewsArticles);
        }

        return this.http.get(securityNewsUrl)
        .pipe(
            map((resp: SecurityNewsResponse) => {
                this.securityNewsArticles = resp.data.slice(0, resp.limit);

                const pictureQueryParams = {
                    format: 'webply',
                    optimize: 'medium'
                };
                const pictureQueryString = this.usefulService.jsonToQueryString(pictureQueryParams);
                for (const article of this.securityNewsArticles) {
                    article.picture = `${article.picture}?${pictureQueryString}`;
                    article.url = this.adobeDataLayerService.addRefAndVisitor(article.url, true);
                }

                return of(this.securityNewsArticles);
            }),
            catchError(err => {
                throw err;
            })
        );
    }

    /**
     * Function that returns all articles
     * @public
     * @memberof SupportService
     *
     * @param {none}
     * @returns {object}
     */
    public getArticles(): object {
        return this.articles;
    }

    /**
     * Function that returns all security news articles
     * @public
     * @memberof SupportService
     *
     * @param {none}
     * @returns {object}
     */
    public getSecurityNewsArticles(): object {
        return this.securityNewsArticles;
    }

    /**
     * Function that returns all search results
     * @public
     * @memberof SupportService
     *
     * @param {none}
     * @returns {object}
     */
    public getSearchResults(): object {
        return this.searchResults;
    }

    /**
     * Function that returns the department id based on language and refund reason
     * @public
     * @memberof SupportService
     *
     * @param {string} category
     * @param {string} product
     * @param {object} refundReasonCategory
     * @param {string} lang
     * @returns {number} representing the mapping code of the category
     */
    public getDepartamentId(category, product, refundReasonCategory, lang): number {
        if (this.valuesSupportService.catMapping.hasOwnProperty(lang) && !refundReasonCategory){
            return  this.valuesSupportService.catMapping[lang][category][product];
        } else if (this.valuesSupportService.catMapping.hasOwnProperty(lang) && refundReasonCategory){
            return  this.valuesSupportService.catMapping[lang][category][refundReasonCategory];
        } else if (!this.valuesSupportService.catMapping.hasOwnProperty(lang) && !refundReasonCategory){
            return  this.valuesSupportService.catMapping[this.valuesService.languages.en_US.id][category][product];
        } else {
            return  this.valuesSupportService.catMapping[this.valuesService.languages.en_US.id][category][refundReasonCategory];
        }
    }

    /**
     * Function that displays the search section based on language
     * @public
     * @memberof SupportService
     *
     * @param {none}
     * @returns {nothing}
     */
    public displaySearchSection(): void {
        if (!this.valuesSupportService.searchArticlesLanguages[this.languageService.getLang()]) {
            this.configService.setSupportSearch(false);
        } else {
            this.configService.setSupportSearch(this.configService.getOriginalSupportSearch());
        }
    }

}
